import React, { useEffect, useState } from "react";
import { fetchLatestValidation, fetchValidationDashboard, fetchValidationDetail } from "../../../lib/researchApi";
import GovernanceBadge from "../shared/GovernanceBadge";
import MetricCard from "../shared/MetricCard";
import OutcomeConfidencePanel from "../shared/OutcomeConfidencePanel";

const STRATEGY_LABELS = {
    wheel: "Wheel",
    earnings: "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread: "Regime Spread",
};

const REGIME_LABELS = {
    bull: "Bull",
    bear: "Bear",
    sideways: "Sideways",
    high_volatility: "High Vol",
    low_volatility: "Low Vol",
    crisis: "Crisis",
    recovery: "Recovery",
};

const REGIME_ORDER = [
    "bull",
    "bear",
    "sideways",
    "high_volatility",
    "low_volatility",
    "crisis",
    "recovery",
];

function fmtPct(value) {
    if (value == null || !Number.isFinite(value)) return "—";
    return `${(value * 100).toFixed(0)}%`;
}

function cellColor(value) {
    if (value == null || !Number.isFinite(value)) return "rgba(255,255,255,0.03)";
    if (value >= 0.75) return "rgba(74, 222, 128, 0.18)";
    if (value >= 0.55) return "rgba(187, 247, 208, 0.12)";
    if (value >= 0.40) return "rgba(251, 191, 36, 0.16)";
    return "rgba(248, 113, 113, 0.18)";
}

function scoreColor(value) {
    if (value == null || !Number.isFinite(value)) return "var(--savvy-dim)";
    if (value >= 0.75) return "#4ade80";
    if (value >= 0.55) return "#86efac";
    if (value >= 0.40) return "#fbbf24";
    return "#f87171";
}

function StrategyRegimeCard({ strategy, strategyDash, regimeDetail }) {
    const summary = regimeDetail?.regime_robustness || {};
    const warnings = summary.warnings || [];
    const weakest = summary.weakest_regimes || [];
    const robustness = strategyDash?.avg_regime_robustness_score;

    return (
        <div
            style={{
                background: "var(--savvy-panel)",
                border: "1px solid var(--savvy-border)",
                borderRadius: 10,
                padding: "16px",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--savvy-text)", marginBottom: 4 }}>
                        {STRATEGY_LABELS[strategy] || strategy}
                    </div>
                    <GovernanceBadge state={strategyDash?.latest_governance_state} />
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 2 }}>Regime Robustness</div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: scoreColor(robustness) }}>
                        {fmtPct(robustness)}
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                {(weakest.length ? weakest : ["No weak regime flagged"]).map((regime) => (
                    <span
                        key={regime}
                        style={{
                            padding: "4px 8px",
                            borderRadius: 999,
                            background: regime === "No weak regime flagged" ? "rgba(255,255,255,0.05)" : "rgba(248,113,113,0.12)",
                            border: regime === "No weak regime flagged" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(248,113,113,0.22)",
                            color: regime === "No weak regime flagged" ? "var(--savvy-dim)" : "#fca5a5",
                            fontSize: "11px",
                            fontWeight: 600,
                        }}
                    >
                        {REGIME_LABELS[regime] || regime}
                    </span>
                ))}
            </div>

            {warnings.length > 0 && (
                <div style={{ display: "grid", gap: 4 }}>
                    {warnings.slice(0, 3).map((warning) => (
                        <div key={warning} style={{ fontSize: "11px", color: "#fbbf24" }}>
                            ⚠ {warning}
                        </div>
                    ))}
                </div>
            )}

            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <OutcomeConfidencePanel
                    outcomeClassification={regimeDetail?.outcome_classification}
                    evidenceConfidence={regimeDetail?.evidence_confidence}
                    compact
                    showUnavailable
                />
            </div>
        </div>
    );
}

export default function RegimeSection() {
    const [dashboard, setDashboard] = useState(null);
    const [details, setDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const dash = await fetchValidationDashboard();
                if (cancelled) return;
                const strategies = Object.keys(dash?.strategies || {});
                const detailPairs = await Promise.all(
                    strategies.map(async (strategy) => {
                        const latest = await fetchLatestValidation(strategy);
                        if (!latest?.id) return [strategy, null];
                        const detail = await fetchValidationDetail(latest.id);
                        return [strategy, detail];
                    })
                );
                if (cancelled) return;
                setDashboard(dash?.strategies || {});
                setDetails(Object.fromEntries(detailPairs));
            } catch (e) {
                if (!cancelled) {
                    setError(e?.response?.data?.error || "Failed to load regime robustness");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return <div style={{ color: "var(--savvy-muted)", padding: 24, textAlign: "center", fontSize: "13px" }}>Loading regime robustness…</div>;
    }
    if (error) {
        return <div style={{ color: "#f87171", padding: 16, fontSize: "13px" }}>Error: {error}</div>;
    }

    const strategies = Object.keys(dashboard || {});
    const avgRobustnessValues = strategies
        .map((strategy) => dashboard?.[strategy]?.avg_regime_robustness_score)
        .filter((value) => typeof value === "number" && Number.isFinite(value));
    const avgRobustness = avgRobustnessValues.length
        ? avgRobustnessValues.reduce((sum, value) => sum + value, 0) / avgRobustnessValues.length
        : null;
    const fragileCount = strategies.filter((strategy) => {
        const flags = details?.[strategy]?.regime_robustness?.alert_flags || {};
        return Object.values(flags).some(Boolean);
    }).length;
    const weakestMentions = strategies.reduce((sum, strategy) => sum + ((details?.[strategy]?.regime_robustness?.weakest_regimes || []).length > 0 ? 1 : 0), 0);

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                <MetricCard label="Avg Regime Robustness" value={avgRobustness == null ? "—" : fmtPct(avgRobustness)} color={scoreColor(avgRobustness)} />
                <MetricCard label="Strategies Mapped" value={strategies.length} />
                <MetricCard label="Fragility Flags" value={fragileCount} color={fragileCount > 0 ? "#f87171" : "#4ade80"} warn={fragileCount > 0} />
                <MetricCard label="Weakest Regime Summaries" value={weakestMentions} />
            </div>

            <div
                style={{
                    background: "var(--savvy-panel)",
                    border: "1px solid var(--savvy-border)",
                    borderRadius: 10,
                    padding: "16px",
                    marginBottom: 16,
                }}
            >
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                    Regime Stability Heatmap
                </div>
                <div style={{ display: "grid", gridTemplateColumns: `180px repeat(${REGIME_ORDER.length}, minmax(84px, 1fr))`, gap: 8, alignItems: "stretch" }}>
                    <div />
                    {REGIME_ORDER.map((regime) => (
                        <div key={regime} style={{ fontSize: "10px", color: "var(--savvy-muted)", textAlign: "center", fontWeight: 600 }}>
                            {REGIME_LABELS[regime]}
                        </div>
                    ))}
                    {strategies.map((strategy) => {
                        const regimeMap = details?.[strategy]?.regime_robustness?.regimes || {};
                        return (
                            <React.Fragment key={strategy}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "12px", color: "var(--savvy-text)", fontWeight: 600 }}>
                                    {STRATEGY_LABELS[strategy] || strategy}
                                </div>
                                {REGIME_ORDER.map((regime) => {
                                    const bucket = regimeMap?.[regime];
                                    const stability = bucket?.stability;
                                    return (
                                        <div
                                            key={`${strategy}-${regime}`}
                                            title={bucket ? `${REGIME_LABELS[regime]} · Stability ${fmtPct(stability)} · Win ${fmtPct(bucket.win_rate)} · Drawdown ${fmtPct(bucket.max_drawdown)}` : `${REGIME_LABELS[regime]} · No data`}
                                            style={{
                                                minHeight: 58,
                                                borderRadius: 8,
                                                border: "1px solid rgba(255,255,255,0.08)",
                                                background: cellColor(stability),
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: 2,
                                                padding: "6px 4px",
                                            }}
                                        >
                                            <div style={{ fontSize: "12px", fontWeight: 700, color: scoreColor(stability) }}>
                                                {fmtPct(stability)}
                                            </div>
                                            <div style={{ fontSize: "10px", color: "var(--savvy-dim)" }}>
                                                {bucket?.total_trades ? `${bucket.total_trades} trades` : "—"}
                                            </div>
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {strategies.map((strategy) => (
                    <StrategyRegimeCard
                        key={strategy}
                        strategy={strategy}
                        strategyDash={dashboard?.[strategy]}
                        regimeDetail={details?.[strategy]}
                    />
                ))}
            </div>
        </div>
    );
}
